import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line } from 'recharts';

const Section = ({ title, right, children }) => (
  <div className="dashboard-section">
    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem'}}>
      <h3>{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

export default function DashboardWrapper({ BACKEND_URL }) {
  const { state } = useLocation();
  const initial = state?.analysis || null;
  const [analysis, setAnalysis] = useState(initial);
  const [niches, setNiches] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [platform, setPlatform] = useState('reddit');
  const [selectedNiche, setSelectedNiche] = useState('');
  const [tone, setTone] = useState('helpful, expert, non-promotional');

  const [showModal, setShowModal] = useState(false);
  const [modalText, setModalText] = useState('');
  const [history, setHistory] = useState([]);
  const [modelToggles, setModelToggles] = useState({ overall: true, perplexity: true, gemini: true });
  const [activeTab, setActiveTab] = useState('home');
  const [rerunning, setRerunning] = useState(false);
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [selectedRun, setSelectedRun] = useState(null);
  const [draftHistory, setDraftHistory] = useState([]);
  const [ga4Config, setGa4Config] = useState({
    measurementId: '',
    apiKey: '',
    propertyId: ''
  });
  const [ga4Data, setGa4Data] = useState(null);
  const [ga4Loading, setGa4Loading] = useState(false);
  const [selectedEngine, setSelectedEngine] = useState('all');
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourceTypeFilter, setSourceTypeFilter] = useState([]);
  const [timeRange, setTimeRange] = useState('all');
  const [modalHeader, setModalHeader] = useState('Source Excerpt');

  const kpis = useMemo(() => {
    const m = analysis?.metrics || {};
    return [
      { label: 'Overall Visibility', value: m.overall_visibility != null ? `${m.overall_visibility.toFixed(1)}%` : '--' },
      { label: 'Weighted Score', value: m.weighted_visibility_score != null ? `${m.weighted_visibility_score.toFixed(1)}%` : '--' },
      { label: 'Avg Rank', value: m.average_rank != null ? m.average_rank.toFixed(1) : '--' },
      { label: 'Best Rank', value: m.best_rank ?? '--' },
    ];
  }, [analysis]);

  // Load saved history on mount (separate from analysis loading)
  useEffect(() => {
    const savedHistory = JSON.parse(localStorage.getItem('analysisHistory') || '[]');
    const savedDraftHistory = JSON.parse(localStorage.getItem('draftHistory') || '[]');
    const savedGa4Config = JSON.parse(localStorage.getItem('ga4Config') || '{}');
    // Sort history by timestamp for proper chronological order
    savedHistory.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    setHistory(savedHistory);
    setDraftHistory(savedDraftHistory);
    setGa4Config(prev => ({ ...prev, ...savedGa4Config }));
    
    // If no current analysis but we have history, load the most recent one
    if (!analysis && savedHistory.length > 0) {
      const mostRecent = savedHistory[savedHistory.length - 1]; // Get the last item (newest after sorting)
      if (mostRecent.metrics) {
        setAnalysis({
          metadata: { url: mostRecent.url },
          metrics: mostRecent.metrics,
          generated_queries: mostRecent.queries,
          search_results: mostRecent.search_results
        });
      }
    }
  }, [analysis]);

  // Load analysis-dependent data only when analysis changes from null to actual data
  // Check localStorage first before making API calls
  useEffect(() => {
    if (!analysis?.metadata?.url) return;
    
    const cacheKey = `sources_niches_${analysis.metadata.url}`;
    const savedData = localStorage.getItem(cacheKey);
    
    // Always try to load from cache first if data doesn't exist in state
    if (savedData && (sources.length === 0 || niches.length === 0)) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.sources && parsed.niches) {
          console.log(`Loading cached sources/niches for ${analysis.metadata.url}`);
          setSources(parsed.sources);
          setNiches(parsed.niches);
          if (parsed.niches.length && !selectedNiche) {
            setSelectedNiche(parsed.niches[0].name);
          }
          return; // Don't fetch from API if we have valid cached data
        }
      } catch (e) {
        console.warn('Failed to parse cached sources/niches:', e);
        localStorage.removeItem(cacheKey); // Remove corrupted cache
      }
    }
    
    // Only fetch from API if no valid cached data and state is empty
    if (sources.length === 0 && niches.length === 0) {
      console.log(`Fetching fresh sources/niches for ${analysis.metadata.url}`);
    (async () => {
      try {
        setLoading(true);
          setSourcesLoading(true);
          
        const [n, s] = await Promise.all([
          axios.post(`${BACKEND_URL}/cluster-niches`, { analysis_data: analysis }),
            axios.post(`${BACKEND_URL}/high-impact-sources`, { analysis_data: analysis, limit: 15 }),
        ]);
          
        setNiches(n.data || []);
        setSources(s.data || []);
          
          // Cache the results with URL key
          localStorage.setItem(cacheKey, JSON.stringify({
            sources: s.data || [],
            niches: n.data || [],
            timestamp: new Date().toISOString(),
            url: analysis.metadata.url
          }));
          
          if (n.data && n.data.length && !selectedNiche) setSelectedNiche(n.data[0].name);
      } catch (e) {
        console.error('Dashboard bootstrap failed', e);
      } finally {
        setLoading(false);
          setSourcesLoading(false);
      }
    })();
    } else {
      console.log(`Using existing sources/niches data (${sources.length} sources, ${niches.length} niches)`);
    }
  }, [analysis, BACKEND_URL, sources.length, niches.length, selectedNiche]);

  const fetchGA4Data = useCallback(async () => {
    if (!ga4Config.measurementId || !ga4Config.propertyId || !analysis?.metadata?.url) {
      return;
    }
    
    try {
      setGa4Loading(true);
      
      // Extract domain from analysis URL for filtering
      const domain = new URL(analysis.metadata.url).hostname;
      
      // Query GA4 for AI search traffic
      const response = await axios.post(`${BACKEND_URL}/ga4-analysis`, {
        measurementId: ga4Config.measurementId,
        propertyId: ga4Config.propertyId,
        apiKey: ga4Config.apiKey,
        domain: domain,
        startDate: '30daysAgo',
        endDate: 'today'
      });
      
      setGa4Data(response.data);
    } catch (error) {
      console.error('GA4 data fetch failed:', error);
      setGa4Data({ error: 'Failed to fetch GA4 data. Please check your configuration.' });
    } finally {
      setGa4Loading(false);
    }
  }, [ga4Config, analysis, BACKEND_URL]);

  // Auto-fetch GA4 data when config is complete and analysis is available
  useEffect(() => {
    if (analysis && ga4Config.measurementId && ga4Config.propertyId && ga4Config.apiKey && !ga4Data && !ga4Loading) {
      fetchGA4Data();
    }
  }, [analysis, ga4Config, ga4Data, ga4Loading, fetchGA4Data]);

  const currentNiche = useMemo(() => niches.find(n => n.name === selectedNiche) || null, [niches, selectedNiche]);

  const filteredHistory = useMemo(() => {
    if (!history || history.length === 0) return history;
    
    const now = new Date();
    const cutoffDate = new Date();
    
    switch(timeRange) {
      case '7d':
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case '30d':
        cutoffDate.setDate(now.getDate() - 30);
        break;
      case 'all':
        return history;
      case 'custom':
        return history; // Custom handled by input
      default:
        // Handle custom day ranges like '45d'
        if (timeRange.endsWith('d')) {
          const days = parseInt(timeRange.slice(0, -1));
          if (days > 0) {
            cutoffDate.setDate(now.getDate() - days);
            break;
          }
        }
        return history;
    }
    
    return history.filter(h => new Date(h.timestamp) >= cutoffDate);
  }, [history, timeRange]);

  const normalizeUrl = (inputUrl) => {
    let urlStr = inputUrl.trim();
    if (urlStr.startsWith('https://www.')) return urlStr;
    if (urlStr.startsWith('http://www.')) return 'https://' + urlStr.slice(7);
    if (urlStr.startsWith('https://')) return 'https://www.' + urlStr.slice(8);
    if (urlStr.startsWith('http://')) return 'https://www.' + urlStr.slice(7);
    if (urlStr.startsWith('www.')) return 'https://' + urlStr;
    return 'https://www.' + urlStr;
  };

  const runAnalysis = async () => {
    if (rerunning) return;
    
    let targetUrl;
    if (analysis?.metadata?.url) {
      // Rerun with existing URL
      targetUrl = analysis.metadata.url;
    } else {
      // First run - need URL
      if (!url.trim()) {
        setError('Please enter a valid URL');
        return;
      }
      targetUrl = normalizeUrl(url);
    }
    
    setRerunning(true);
    setError('');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/analyze-website`, {
        url: targetUrl
      });
      
      if (response.data) {
        setAnalysis(response.data);
        
        // Create history item with datetime
        const historyItem = {
          id: Date.now(),
          timestamp: new Date().toISOString(),
          t: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          overall: response.data.metrics?.overall_visibility || 0,
          perplexity: response.data.metrics?.engine_performance?.perplexity?.visibility_percentage || 0,
          gemini: response.data.metrics?.engine_performance?.gemini?.visibility_percentage || 0,
          queries: response.data.generated_queries || [],
          search_results: response.data.search_results || [],
          metrics: response.data.metrics,
          url: targetUrl
        };
        
        // Save to localStorage for persistence across logins
        const savedHistory = JSON.parse(localStorage.getItem('analysisHistory') || '[]');
        const updatedHistory = [...savedHistory, historyItem].slice(-20);
        // Sort by timestamp to ensure proper chronological order
        updatedHistory.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        localStorage.setItem('analysisHistory', JSON.stringify(updatedHistory));
        setHistory(updatedHistory);
        
        // Force reload sources and niches when rerunning analysis
        setSourcesLoading(true);
        setLoading(true);
        
        // Clear cached sources/niches for this URL
        const cacheKey = `sources_niches_${response.data.metadata?.url}`;
        localStorage.removeItem(cacheKey);
        
        Promise.all([
          axios.post(`${BACKEND_URL}/cluster-niches`, { analysis_data: response.data }).catch(() => ({ data: [] })),
          axios.post(`${BACKEND_URL}/high-impact-sources`, { 
            analysis_data: response.data,
            limit: 15 
          }).catch(() => ({ data: [] }))
        ]).then(([n, s]) => {
          setNiches(n.data || []);
          setSources(s.data || []);
          
          // Cache the new results
          localStorage.setItem(cacheKey, JSON.stringify({
            sources: s.data || [],
            niches: n.data || [],
            timestamp: new Date().toISOString()
          }));
          
          if (n.data && n.data.length && !selectedNiche) setSelectedNiche(n.data[0].name);
          setSourcesLoading(false);
          setLoading(false);
        });
        
        setUrl('');
      }
    } catch (error) {
      console.error('Error running analysis:', error);
      setError('Analysis failed. Please try again.');
    } finally {
      setRerunning(false);
    }
  };

  const generateDraft = async (targetPlatform) => {
    if (!analysis) return;
    try {
      setGenLoading(true);
      const resp = await axios.post(`${BACKEND_URL}/generate-draft`, {
        platform: targetPlatform || platform,
        brand_url: analysis?.metadata?.url,
        niche: selectedNiche || undefined,
        tone,
        extra_context: {
          niche_keywords: currentNiche?.keywords || [],
          example_queries: currentNiche?.queries?.slice(0, 5) || [],
        },
      });
      
      // Enhanced draft parsing logic
      let draftData = resp.data;
      
      console.log('Raw draft response:', draftData, typeof draftData);
      
      // Helper function to extract JSON from markdown code blocks
      const extractJsonFromCodeBlocks = (text) => {
        if (typeof text !== 'string') return text;
        
        // Look for code blocks that might contain JSON
        const codeBlockRegex = /```(?:json)?\s*(.*?)```/s;
        const match = text.match(codeBlockRegex);
        
        if (match) {
          const content = match[1].trim();
          // Check if the content looks like JSON (starts with { and ends with })
          if (content.startsWith('{') && content.endsWith('}')) {
            return content;
          }
        }
        
        return text;
      };

      // Handle different response formats
      if (typeof draftData === 'string') {
        // First try to extract from code blocks
        const cleanedData = extractJsonFromCodeBlocks(draftData);
        
        console.log('Cleaned data:', cleanedData);
        
        // Try to parse as JSON
        try {
          const parsed = JSON.parse(cleanedData);
          // If it parses successfully and has expected fields, use the parsed version
          if (parsed && (parsed.title || parsed.body || parsed.content || parsed.suggestions)) {
            console.log('Successfully parsed cleaned data as JSON:', parsed);
            draftData = parsed;
          } else {
            // If it's just a JSON string without the expected structure, treat as plain text
            draftData = { 
              title: '', 
              body: cleanedData.startsWith('{') ? 'Generated content received. Please check format.' : cleanedData,
              suggestions: []
            };
          }
        } catch (e) {
          console.log('Failed to parse cleaned data, trying original:', e);
          // Try parsing original string if cleaned version fails
          try {
            const parsed = JSON.parse(draftData);
            if (parsed && (parsed.title || parsed.body || parsed.content || parsed.suggestions)) {
              console.log('Successfully parsed original data as JSON:', parsed);
              draftData = parsed;
            } else {
              draftData = { 
                title: '',
                body: draftData,
                suggestions: []
              };
            }
          } catch (e2) {
            console.log('Failed to parse original data, treating as plain text:', e2);
            // Not valid JSON, treat as plain text content
            draftData = { 
              title: '',
              body: draftData,
              suggestions: []
            };
          }
        }
      } else if (typeof draftData === 'object' && draftData !== null) {
        // Already an object, ensure it has the right structure
        if (!draftData.body && !draftData.content && !draftData.text) {
          // Object but no recognizable content field
          draftData = {
            title: draftData.title || '',
            body: 'Draft generated. Content structure may need adjustment.',
            suggestions: Array.isArray(draftData.suggestions) ? draftData.suggestions : []
          };
        }
      } else {
        // Fallback for unexpected types
        draftData = {
          title: '',
          body: 'Unexpected response format received.',
          suggestions: []
        };
      }
      
      console.log('Processed draft data:', draftData);
      setDraft(draftData);
      
      // Save to draft history
      const draftHistoryItem = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        platform: targetPlatform || platform,
        niche: selectedNiche,
        tone,
        content: draftData,
        url: analysis?.metadata?.url
      };
      
      const savedDraftHistory = JSON.parse(localStorage.getItem('draftHistory') || '[]');
      const updatedDraftHistory = [draftHistoryItem, ...savedDraftHistory].slice(0, 50);
      localStorage.setItem('draftHistory', JSON.stringify(updatedDraftHistory));
      setDraftHistory(updatedDraftHistory);
      
    } catch (e) {
      console.error('Draft generation failed', e);
    } finally {
      setGenLoading(false);
    }
  };

  const saveGA4Config = (newConfig) => {
    setGa4Config(newConfig);
    localStorage.setItem('ga4Config', JSON.stringify(newConfig));
  };

  if (!analysis) {
    return (
      <div className="container dashboard-page">
        <div style={{marginBottom:'1rem', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <div>
            <h2 className="section-title" style={{textAlign:'left'}}>Dashboard</h2>
            <p className="subtle">Enter your website URL to get started with AI search analysis</p>
        </div>
      </div>
        
        <div className="dashboard-section" style={{padding:'2rem', textAlign:'center'}}>
          <h3>Analyze Your Website</h3>
          <p className="subtle" style={{marginBottom:'1.5rem'}}>
            Enter your website URL to get comprehensive AI search engine analysis and optimization recommendations.
          </p>
          
          <div style={{maxWidth:'600px', margin:'0 auto'}}>
            <div style={{display:'flex', gap:'1rem', marginBottom:'1rem'}}>
              <input
                type="text"
                placeholder="Enter your website URL (e.g., https://example.com)"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={rerunning}
                style={{
                  flex:1,
                  padding:'1rem',
                  border:'2px solid rgba(59, 130, 246, 0.2)',
                  borderRadius:'8px',
                  background:'rgba(13, 26, 56, 0.8)',
                  color:'#f8fafc',
                  fontSize:'1rem'
                }}
              />
              <button 
                onClick={runAnalysis} 
                disabled={rerunning || !url.trim()}
                className="btn primary"
                style={{display:'flex', alignItems:'center', gap:'0.5rem', whiteSpace:'nowrap'}}
              >
                {rerunning && <div className="loading-spinner-circle"></div>}
                {rerunning ? 'Analyzing...' : 'Run Analysis'}
              </button>
      </div>
            
            {error && <div style={{color:'#ef4444', marginTop:'0.5rem'}}>{error}</div>}
            
            <div className="subtle" style={{fontSize:'0.9rem'}}>
              Supported engines: Perplexity & Gemini (OpenAI, Bing, and more coming soon)
                </div>
            </div>
            </div>
                </div>
    );
  }

  const renderTabContent = () => {
    switch(activeTab) {
      case 'home':
        return (
          <div>
            <Section title="Brand Visibility Over Time" right={
              <div style={{display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap'}}>
                {['all', '7d', '30d', 'custom'].map(range => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`btn ${timeRange === range ? 'primary' : 'secondary'}`}
                    style={{fontSize:'0.8rem', padding:'0.4rem 0.8rem'}}
                  >
                    {range === 'all' ? 'All Time' : 
                     range === '7d' ? 'Last 7 Days' : 
                     range === '30d' ? 'Last 30 Days' : 'Custom Range'}
                  </button>
                ))}
                {timeRange === 'custom' && (
                  <input
                    type="number"
                    placeholder="Days"
                    min="1"
                    max="365"
                    style={{
                      width:'60px',
                      padding:'0.4rem',
                      background:'rgba(13, 26, 56, 0.8)',
                      border:'1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius:'4px',
                      color:'#f8fafc',
                      fontSize:'0.8rem'
                    }}
                    onChange={(e) => {
                      const days = parseInt(e.target.value);
                      if (days > 0) {
                        setTimeRange(`${days}d`);
                      }
                    }}
                  />
                )}
              </div>
            }>
              <div className="dashboard-home-layout">
                <div className="main-chart-section">
                <div className="actions-row" style={{marginBottom:'0.75rem'}}>
                  {['overall','perplexity','gemini'].map(key => (
                    <label key={key} style={{display:'flex', alignItems:'center', gap:'0.4rem'}}>
                      <input type="checkbox" checked={!!modelToggles[key]} onChange={(e)=>setModelToggles(prev=>({...prev,[key]:e.target.checked}))} />
                      <span className="subtle">{key}</span>
                    </label>
                  ))}
                </div>
                  <div className="main-chart-container">
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={(filteredHistory && filteredHistory.length) ? filteredHistory.map(h => ({
                        ...h, 
                        t: `${new Date(h.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} @ ${new Date(h.timestamp).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })}`
                      })) : [{ t: `${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} @ ${new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })}`, overall: analysis?.metrics?.overall_visibility || 0, perplexity: analysis?.metrics?.engine_performance?.perplexity?.visibility_percentage || 0, gemini: analysis?.metrics?.engine_performance?.gemini?.visibility_percentage || 0 }] }>
                        <defs>
                          <linearGradient id="overallGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis dataKey="t" stroke="#94a3b8" />
                        <YAxis domain={[0,100]} stroke="#94a3b8" />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: 'rgba(30, 41, 59, 0.95)',
                            border: '1px solid #3b82f6',
                            borderRadius: '8px',
                            color: '#f8fafc'
                          }}
                        />
                      <Legend />
                        {modelToggles.overall && <Line type="linear" dataKey="overall" stroke="#3b82f6" strokeWidth={3} fill="url(#overallGradient)" name="Overall %" dot={{fill: '#3b82f6', strokeWidth: 2, r: 4}} />}
                        {modelToggles.perplexity && <Line type="linear" dataKey="perplexity" stroke="#06b6d4" strokeWidth={2} name="Perplexity %" dot={{fill: '#06b6d4', strokeWidth: 2, r: 3}} />}
                        {modelToggles.gemini && <Line type="linear" dataKey="gemini" stroke="#8b5cf6" strokeWidth={2} name="Gemini %" dot={{fill: '#8b5cf6', strokeWidth: 2, r: 3}} />}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                  
                  {/* Quick Insights Section - Under Graph */}
                  <div className="quick-insights-under-chart">
                    <div className="insights-grid-compact">
                      <div className="insight-card-small">
                        <div className="insight-icon"></div>
                        <div className="insight-content">
                          <div className="insight-title">Best Engine</div>
                          <div className="insight-value">
                            {analysis?.metrics?.engine_performance?.perplexity?.visibility_percentage > analysis?.metrics?.engine_performance?.gemini?.visibility_percentage ? 'Perplexity' : 'Gemini'}
              </div>
                        </div>
                      </div>
                      
                      <div className="insight-card-small">
                        <div className="insight-icon"></div>
                        <div className="insight-content">
                          <div className="insight-title">Opportunities</div>
                          <div className="insight-value">
                            {analysis?.metrics?.missed_queries?.length || 0} missed
                          </div>
                        </div>
                      </div>
                      
                      <div className="insight-card-small">
                        <div className="insight-icon"></div>
                        <div className="insight-content">
                          <div className="insight-title">Next Action</div>
                          <div className="insight-value">
                            {sources.length > 0 ? 'Review Sources' : 'Generate Content'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="insight-card-small">
                        <div className="insight-icon"></div>
                        <div className="insight-content">
                          <div className="insight-title">Overall Score</div>
                          <div className="insight-value">
                            {analysis?.metrics?.overall_visibility > 70 ? 'Excellent' : 
                             analysis?.metrics?.overall_visibility > 40 ? 'Good' : 'Needs Work'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="right-panel">
                  {/* Engine Performance Chart */}
                  {analysis?.metrics?.engine_performance && (
                    <div className="small-chart">
                      <h4>Engine Performance</h4>
                      <p className="chart-description">Shows how well your site ranks in each AI search engine</p>
                      <div className="engine-performance-grid" style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem', marginTop:'0.5rem'}}>
                        {Object.entries(analysis.metrics.engine_performance).map(([engine, perf]) => {
                          // Calculate change from previous run if available
                          const previousPerf = history && history.length > 1 ? history[history.length - 2]?.[engine] : null;
                          const currentPerf = perf.visibility_percentage;
                          const change = previousPerf ? currentPerf - previousPerf : null;
                          const hasChange = change !== null && Math.abs(change) > 0.1;
                          
                          return (
                            <div key={engine} className="engine-perf-card" style={{
                              padding:'0.5rem',
                              background:'rgba(13, 26, 56, 0.6)',
                              borderRadius:'6px',
                              border:'1px solid rgba(59, 130, 246, 0.2)',
                              textAlign:'center'
                            }}>
                              <div style={{fontSize:'0.8rem', color:'#94a3b8', marginBottom:'0.25rem'}}>
                                {engine.charAt(0).toUpperCase() + engine.slice(1)}
                              </div>
                              <div style={{fontSize:'1.5rem', fontWeight:'700', color:'#f8fafc', marginBottom:'0.25rem'}}>
                                {Math.round(currentPerf)}%
                              </div>
                              {hasChange && (
                                <div style={{
                                  fontSize:'0.7rem',
                                  color: change > 0 ? '#22c55e' : '#ef4444',
                                  fontWeight:'600'
                                }}>
                                  {change > 0 ? '▲' : '▼'} {Math.abs(change).toFixed(1)}%
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Top Sources */}
                  <div className="top-sources">
                    <h4>Top Sources</h4>
                    {sourcesLoading ? <div className="subtle">Loading…</div> : (
                      <div className="sources-list">
                        {sources.slice(0,4).map(s => {
                          // Enhanced site type detection function
                          const getTopSourcesSiteType = (source) => {
                            // First try to use the source_type from API
                            if (source.source_type && source.source_type !== 'reference' && source.source_type !== 'web') {
                              return source.source_type;
                            }
                            
                            // Enhanced domain-based detection (same as External References)
                            const domain = source.domain.toLowerCase();
                            
                            // Social platforms & forums
                            if (domain.includes('reddit.com') || domain.includes('forums') || domain.includes('forum') || 
                                domain.includes('discourse') || domain.includes('community') || domain.includes('discussion')) return 'forum';
                            if (domain.includes('stackoverflow.com') || domain.includes('stackexchange.com')) return 'tech-forum';
                            if (domain.includes('twitter.com') || domain.includes('facebook.com') || domain.includes('linkedin.com') || 
                                domain.includes('instagram.com') || domain.includes('tiktok.com')) return 'social';
                            
                            // Developer & tech
                            if (domain.includes('github.com') || domain.includes('gitlab.com') || domain.includes('bitbucket.org')) return 'code';
                            if (domain.includes('docs') || domain.includes('documentation') || domain.includes('api') || 
                                domain.includes('developer') || domain.includes('devdocs')) return 'docs';
                            
                            // Content & media
                            if (domain.includes('medium.com') || domain.includes('blog') || domain.includes('wordpress') || 
                                domain.includes('substack') || domain.includes('hashnode')) return 'blog';
                            if (domain.includes('youtube.com') || domain.includes('vimeo.com') || domain.includes('twitch.tv')) return 'video';
                            if (domain.includes('podcast') || domain.includes('spotify.com') || domain.includes('apple.com/podcasts')) return 'podcast';
                            
                            // News & media
                            if (domain.includes('news') || domain.includes('times') || domain.includes('post') || domain.includes('herald') ||
                                domain.includes('tribune') || domain.includes('guardian') || domain.includes('reuters') || 
                                domain.includes('bloomberg') || domain.includes('cnn') || domain.includes('bbc')) return 'news';
                            
                            // E-commerce
                            if (domain.includes('shop') || domain.includes('store') || domain.includes('amazon') || domain.includes('ebay') ||
                                domain.includes('etsy') || domain.includes('shopify') || domain.includes('ecommerce')) return 'shop';
                            
                            // Educational & reference
                            if (domain.includes('wiki') || domain.includes('wikipedia')) return 'wiki';
                            if (domain.includes('edu') || domain.includes('university') || domain.includes('college') || 
                                domain.includes('school') || domain.includes('course') || domain.includes('learn')) return 'education';
                            
                            // Reviews & recommendations
                            if (domain.includes('review') || domain.includes('rating') || domain.includes('recommend') || 
                                domain.includes('yelp') || domain.includes('tripadvisor') || domain.includes('trustpilot')) return 'review';
                            
                            // Smarter default fallback  
                            if (domain.includes('.com') || domain.includes('.org') || domain.includes('.net')) {
                              return 'business';
                            }
                            return 'site';
                          };
                          
                          return (
                            <div key={s.domain} className="source-item">
                              <div className="source-main">
                                <div className="source-domain-container" style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.25rem'}}>
                                  <div style={{display:'flex', alignItems:'center', gap:'0.5rem', flex:1, minWidth:0}}>
                                    <div className="source-domain" style={{fontWeight:600}}>{s.domain}</div>
                                    <span className="source-type-tag" style={{fontSize:'0.7rem', padding:'0.2rem 0.4rem'}}>{getTopSourcesSiteType(s)}</span>
                                  </div>
                                  <div className="source-meta" style={{marginLeft:'0.75rem', flexShrink:0}}>{s.count} mentions</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  {/* Success Rate - Separate section */}
                  {(analysis?.metrics?.successful_queries || analysis?.metrics?.missed_queries) && (
                    <div className="success-rate-section">
                      <h4>Query Success Rate</h4>
                      <div className="success-rate-display">
                        <div className="success-rate-number">
                          {Math.round((analysis?.metrics?.successful_queries?.length || 0) / 
                          ((analysis?.metrics?.successful_queries?.length || 0) + (analysis?.metrics?.missed_queries?.length || 0)) * 100)}%
                        </div>
                        <div className="success-rate-label">
                          {analysis?.metrics?.successful_queries?.length || 0} of {(analysis?.metrics?.successful_queries?.length || 0) + (analysis?.metrics?.missed_queries?.length || 0)} queries found your site
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </Section>

          </div>
        );
      case 'sources':
        const getSiteType = (source) => {
          // First try to use the source_type from API
          if (source.source_type && source.source_type !== 'reference' && source.source_type !== 'web') {
            return source.source_type;
          }
          
          // Enhanced domain-based detection
          const domain = source.domain.toLowerCase();
          
          // Social platforms & forums
          if (domain.includes('reddit.com') || domain.includes('forums') || domain.includes('forum') || 
              domain.includes('discourse') || domain.includes('community') || domain.includes('discussion')) return 'forum';
          if (domain.includes('stackoverflow.com') || domain.includes('stackexchange.com')) return 'tech-forum';
          if (domain.includes('twitter.com') || domain.includes('facebook.com') || domain.includes('linkedin.com') || 
              domain.includes('instagram.com') || domain.includes('tiktok.com')) return 'social';
          
          // Developer & tech
          if (domain.includes('github.com') || domain.includes('gitlab.com') || domain.includes('bitbucket.org')) return 'code';
          if (domain.includes('docs') || domain.includes('documentation') || domain.includes('api') || 
              domain.includes('developer') || domain.includes('devdocs')) return 'docs';
          
          // Content & media
          if (domain.includes('medium.com') || domain.includes('blog') || domain.includes('wordpress') || 
              domain.includes('substack') || domain.includes('hashnode')) return 'blog';
          if (domain.includes('youtube.com') || domain.includes('vimeo.com') || domain.includes('twitch.tv')) return 'video';
          if (domain.includes('podcast') || domain.includes('spotify.com') || domain.includes('apple.com/podcasts')) return 'podcast';
          
          // News & media
          if (domain.includes('news') || domain.includes('times') || domain.includes('post') || domain.includes('herald') ||
              domain.includes('tribune') || domain.includes('guardian') || domain.includes('reuters') || 
              domain.includes('bloomberg') || domain.includes('cnn') || domain.includes('bbc')) return 'news';
          
          // E-commerce
          if (domain.includes('shop') || domain.includes('store') || domain.includes('amazon') || domain.includes('ebay') ||
              domain.includes('etsy') || domain.includes('shopify') || domain.includes('ecommerce')) return 'shop';
          
          // Educational & reference
          if (domain.includes('wiki') || domain.includes('wikipedia')) return 'wiki';
          if (domain.includes('edu') || domain.includes('university') || domain.includes('college') || 
              domain.includes('school') || domain.includes('course') || domain.includes('learn')) return 'education';
          
          // Reviews & recommendations
          if (domain.includes('review') || domain.includes('rating') || domain.includes('recommend') || 
              domain.includes('yelp') || domain.includes('tripadvisor') || domain.includes('trustpilot')) return 'review';
          
          // Smarter default fallback
          if (domain.includes('.com') || domain.includes('.org') || domain.includes('.net')) {
            return 'business';
          }
          return 'site';
        };

        const siteTypes = [...new Set(sources.map(s => getSiteType(s)))];
        const filteredSources = sourceTypeFilter.length === 0 
          ? sources 
          : sources.filter(s => sourceTypeFilter.includes(getSiteType(s)));
        
        return (
          <Section title="External References" right={
            <div style={{display:'flex', gap:'0.5rem', flexWrap:'wrap'}}>
              {sourceTypeFilter.length > 0 && (
                <button
                  onClick={() => setSourceTypeFilter([])}
                  style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '12px',
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.7rem',
                    color: '#ef4444',
                    cursor: 'pointer',
                    marginRight: '0.5rem'
                  }}
                >
                  Clear
                </button>
              )}
              {siteTypes.map(type => (
                <button
                  key={type}
                  onClick={() => {
                    setSourceTypeFilter(prev => 
                      prev.includes(type) 
                        ? prev.filter(t => t !== type)
                        : [...prev, type]
                    );
                  }}
                  className={`filter-tag ${sourceTypeFilter.includes(type) ? 'active' : ''}`}
                  style={{
                    background: sourceTypeFilter.includes(type) ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                    border: '1px solid rgba(59, 130, 246, 0.4)',
                    borderRadius: '12px',
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.7rem',
                    color: sourceTypeFilter.includes(type) ? '#3b82f6' : '#94a3b8',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
          }>
            {sourcesLoading ? (
              <div className="subtle">Loading external references…</div>
            ) : (
              <div className="two-col external-references">
                {filteredSources.slice(0,12).map((s, idx) => (
                  <div key={s.domain} 
                       className="dashboard-section clickable-source" 
                       style={{
                         padding:'1rem',
                         cursor: 'pointer',
                         transition: 'all 0.2s ease'
                       }}
                       onClick={async () => {
                         if (!s.mention_context) {
                           // Enhanced mention context with more details
                           setModalText('<div style="display: flex; align-items: center; gap: 0.5rem; color: #94a3b8;"><div class="loading-spinner-circle"></div>Loading mention context...</div>');
                           setShowModal(true);
                           setModalHeader(`${s.domain} - Brand Mention Context`);
                           
                           try {
                             const response = await axios.post(`${BACKEND_URL}/get-mention-context`, {
                               domain: s.domain,
                               url: analysis?.metadata?.url,
                               source_type: getSiteType(s),
                               queries: analysis?.generated_queries?.slice(0, 3) || [],
                               brand_name: analysis?.metadata?.url?.replace(/^https?:\/\/(www\.)?/, '').split('/')[0],
                               timeout: 10000 // Add timeout
                             });
                             
                             const context = response.data.context || response.data.summary || response.data.excerpt || '';
                             if (context) {
                               // Format the context with brand mentions highlighted
                               const brandName = analysis?.metadata?.url?.replace(/^https?:\/\/(www\.)?/, '').split('/')[0];
                               const highlightedContext = context
                                 .replace(new RegExp(`(${brandName})`, 'gi'), '<span class="glow-link" style="background: rgba(59, 130, 246, 0.2); padding: 0.1rem 0.3rem; border-radius: 3px; font-weight: 600;">$1</span>')
                                 .replace(/(https?:\/\/[^\s]+)/gi, '<a href="$1" target="_blank" class="glow-link">$1</a>');
                               
                               s.mention_context = `
                                 <div style="margin-bottom: 1rem;">
                                   <div style="font-weight: 600; color: #3b82f6; margin-bottom: 0.5rem;">Brand Mention Context</div>
                                   <div style="font-size: 0.8rem; color: #94a3b8; margin-bottom: 0.5rem;">
                                     Source: ${s.domain} • Type: ${getSiteType(s)} • Mentions: ${s.count} • Relevance: ${(s.score * 100).toFixed(1)}%
                                   </div>
                                 </div>
                                 <div style="line-height: 1.6; color: #f8fafc;">
                                   ${highlightedContext}
                                 </div>
                               `;
                               setModalText(s.mention_context);
                             } else {
                               throw new Error('No context found');
                             }
                           } catch (e) {
                             console.warn('Failed to fetch mention context for', s.domain, ':', e);
                             
                             // Enhanced fallback with more useful information
                             const contextByType = {
                               'blog': `This blog (${s.domain}) was mentioned ${s.count} times in AI search results. It appears to be a relevant content source that discusses topics related to your website. Consider reaching out for potential collaborations or guest posting opportunities.`,
                               'forum': `This forum (${s.domain}) mentioned your site ${s.count} times. Forums are great places for community engagement and getting organic mentions. Consider participating in relevant discussions.`,
                               'tech-forum': `This technical forum (${s.domain}) mentioned your site ${s.count} times. Tech forums are valuable for developer credibility and technical authority.`,
                               'news': `This news outlet (${s.domain}) referenced your site ${s.count} times. News mentions can significantly boost domain authority and credibility. This is a high-value mention source.`,
                               'social': `This social platform (${s.domain}) mentioned your site ${s.count} times. Social mentions can drive traffic and increase brand awareness.`,
                               'code': `This developer platform (${s.domain}) referenced your site ${s.count} times, likely in technical discussions or documentation.`,
                               'docs': `This documentation site (${s.domain}) referenced your site ${s.count} times, indicating technical credibility and developer trust.`,
                               'video': `This video platform (${s.domain}) mentioned your brand ${s.count} times. Video mentions can significantly boost brand awareness and engagement.`,
                               'podcast': `This podcast platform (${s.domain}) mentioned your brand ${s.count} times. Podcast mentions indicate thought leadership and industry recognition.`,
                               'review': `This review site (${s.domain}) mentioned your brand ${s.count} times. Review sites can significantly impact purchasing decisions.`,
                               'education': `This educational resource (${s.domain}) referenced your site ${s.count} times, indicating academic or learning value.`,
                               'shop': `This e-commerce site (${s.domain}) mentioned your brand ${s.count} times, potentially as a competitor or comparison.`,
                               'wiki': `This wiki resource (${s.domain}) referenced your site ${s.count} times, indicating authoritative information value.`,
                               'business': `This business website (${s.domain}) mentioned your brand ${s.count} times, indicating industry recognition and potential partnership opportunities.`,
                               'site': `This website (${s.domain}) mentioned your brand ${s.count} times across various content types.`
                             };
                             
                             // Get actual site type for this source using our enhanced detection
                             const actualSiteType = getSiteType(s);
                             const fallbackContext = contextByType[actualSiteType] || 
                               `${s.domain} (${actualSiteType}) mentioned your site ${s.count} times with a relevance score of ${(s.score * 100).toFixed(1)}%. This indicates ${s.score > 0.8 ? 'high relevance' : s.score > 0.5 ? 'moderate relevance' : 'some relevance'} to your brand or industry. Consider analyzing these mentions for partnership opportunities or competitive insights.`;
                             
                             s.mention_context = `
                               <div style="margin-bottom: 1rem;">
                                 <div style="font-weight: 600; color: #ef4444; margin-bottom: 0.5rem;">⚠️ Context Unavailable</div>
                                 <div style="font-size: 0.8rem; color: #94a3b8; margin-bottom: 0.5rem;">
                                   Source: ${s.domain} • Type: ${getSiteType(s)} • Mentions: ${s.count} • Relevance: ${(s.score * 100).toFixed(1)}%
                                 </div>
                               </div>
                               <div style="line-height: 1.6; color: #f8fafc;">
                                 ${fallbackContext}
                               </div>
                               <div style="margin-top: 1rem; padding: 0.75rem; background: rgba(59, 130, 246, 0.1); border-radius: 6px; border-left: 3px solid #3b82f6;">
                                 <div style="font-size: 0.8rem; color: #94a3b8;">
                                   💡 <strong>Tip:</strong> Visit <a href="https://${s.domain}" target="_blank" class="glow-link">${s.domain}</a> directly to find specific mentions of your brand.
                                 </div>
                               </div>
                             `;
                             
                             setModalText(s.mention_context);
                           }
                         } else {
                           // Show cached context
                           setShowModal(true);
                           setModalHeader(`${s.domain} - Brand Mention Context`);
                           setModalText(s.mention_context);
                         }
                       }}>
                    <div style={{display:'flex', justifyContent:'space-between'}}>
                      <div style={{fontWeight:700}}>{s.domain}</div>
                      <div className="badge">{getSiteType(s)}</div>
                    </div>
                    <div className="subtle">Score: {s.score} • Mentions: {s.count}</div>
                    <div className="subtle" style={{marginTop:'0.5rem', fontSize:'0.8rem'}}>
                      Click to view mention context • 
                      <a 
                        className="glow-link" 
                        href={s.sample_url} 
                        target="_blank" 
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{marginLeft:'0.5rem'}}
                      >
                        Open source
                      </a>
                      </div>
                    {s.niches?.length ? (
                      <div style={{marginTop:'0.5rem'}}>
                        {s.niches.slice(0,4).map(n => (<span className="pill" key={n}>{n}</span>))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </Section>
        );
      case 'history':
        if (selectedRun) {
          return (
            <Section title="Run History" right={<button className="btn secondary" onClick={() => setSelectedRun(null)}>← Back</button>}>
              <div className="dashboard-section" style={{padding:'1rem'}}>
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:'1rem'}}>
                  <div>
                    <div style={{fontWeight:700, fontSize:'1.1rem'}}>{new Date(selectedRun.timestamp).toLocaleString()}</div>
                    <div className="subtle">{selectedRun.url}</div>
                  </div>
                  <div className="badge" style={{
                    fontSize:'0.9rem', 
                    padding:'0.3rem 0.7rem', 
                    height:'auto',
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'center'
                  }}>
                    Overall {Math.round(selectedRun.overall)}%
                  </div>
                </div>
                
                {/* Engine selector */}
                <div style={{marginBottom:'1rem'}}>
                  <div className="actions-row">
                    {['all', 'perplexity', 'gemini'].map(engine => (
                      <button
                        key={engine}
                        className={`btn ${selectedEngine === engine ? 'primary' : 'secondary'}`}
                        style={{fontSize:'0.8rem', padding:'0.5rem 0.75rem'}}
                        onClick={() => setSelectedEngine(engine)}
                      >
                        {engine === 'all' ? 'All Engines' : engine.charAt(0).toUpperCase() + engine.slice(1)}
                      </button>
                    ))}
                </div>
                  
                  <div className="subtle" style={{marginTop:'0.5rem'}}>
                    {selectedEngine === 'all' && (
                      <>Perplexity {Math.round(selectedRun.perplexity||0)}% • Gemini {Math.round(selectedRun.gemini||0)}%</>
                    )}
                    {selectedEngine === 'perplexity' && (
                      <>Perplexity visibility: {Math.round(selectedRun.perplexity||0)}%</>
                    )}
                    {selectedEngine === 'gemini' && (
                      <>Gemini visibility: {Math.round(selectedRun.gemini||0)}%</>
                    )}
                </div>
              </div>
                
                {selectedRun.queries && selectedRun.queries.length > 0 && (
                  <div>
                    <div style={{fontWeight:600, marginBottom:'0.5rem'}}>
                      Search Queries ({selectedRun.search_results?.filter(result => {
                        if (selectedEngine === 'all') return true;
                        return result.engine === selectedEngine;
                      }).length || 0}):
                    </div>
                    <div style={{display:'grid', gap:'0.5rem'}}>
                      {selectedRun.search_results
                        ?.filter(result => {
                          if (selectedEngine === 'all') return true;
                          return result.engine === selectedEngine;
                        })
                        .map((result, qIdx) => {
                          const found = result?.found;
                          const brandMentioned = result?.brand_mentioned;
                          
                          return (
                            <div 
                              key={qIdx} 
                              className="clickable-run"
                              style={{
                                padding:'0.75rem',
                                background: found ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                borderRadius:'8px',
                                fontSize:'0.9rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => {
                                setShowModal(true);
                                setModalHeader('Full Transcript'); // Set different header for query responses
                                
                                // Try multiple possible response fields
                                const responseText = result.response || result.ai_response || result.full_response || result.answer || '';
                                console.log('Query result data:', result); // Debug log
                                
                                // Format and highlight the response text
                                const brandName = analysis?.metadata?.url?.replace(/^https?:\/\/(www\.)?/, '').split('/')[0] || '';
                                const brandDomain = brandName.toLowerCase();
                                
                                const highlightedResponse = responseText ? responseText
                                  // Format text for better readability
                                  .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #f8fafc; font-weight: 700;">$1</strong>') // Bold text
                                  .replace(/\*(.*?)\*/g, '<em style="color: #e2e8f0; font-style: italic;">$1</em>') // Italic text
                                  .replace(/\n\n/g, '</p><p style="margin-bottom: 1rem;">') // Paragraph breaks
                                  .replace(/\n- /g, '<br/>• ') // Bullet points
                                  .replace(/\n(\d+\. )/g, '<br/>$1') // Numbered lists
                                  .replace(/\[(\d+)\]/g, '<sup style="color: #06b6d4; font-size: 0.7rem;">[$1]</sup>') // Citations
                                  // Only highlight the target brand/website being analyzed
                                  .replace(
                                    new RegExp(`\\b(${brandName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}|${brandDomain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi'),
                                    '<span class="glow-link" style="background: rgba(59, 130, 246, 0.2); color: #38bdf8; padding: 0.2rem 0.4rem; border-radius: 4px; font-weight: 700; text-shadow: 0 0 8px #38bdf8;">$1</span>'
                                  ) : '';
                                
                                // Wrap in paragraph tags if not already formatted
                                const formattedResponse = highlightedResponse.includes('<p') ? 
                                  highlightedResponse : 
                                  `<p style="margin-bottom: 1rem;">${highlightedResponse}</p>`;
                                
                                setModalText(`
                                  <div style="margin-bottom: 1rem;">
                                    <div style="font-weight: 600; color: #3b82f6; margin-bottom: 0.5rem;">Query: "${result.query}"</div>
                                    <div style="font-size: 0.8rem; color: #94a3b8; margin-bottom: 0.5rem;">
                                      Engine: ${result.engine?.charAt(0).toUpperCase() + result.engine?.slice(1)} | ${found ? `Rank ${result.rank}` : 'Not found'} ${brandMentioned ? '| Brand Mentioned' : ''}
                                    </div>
                                  </div>
                                  <div style="line-height: 1.6; color: #f8fafc;">
                                    ${formattedResponse || 'Response data not available for this query. Please check the backend logs.'}
                                  </div>
                                `);
                              }}
                            >
                              <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.5rem'}}>
                                <span style={{fontWeight:600}}>"{result.query}"</span>
                                <div style={{display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap'}}>
                                  {brandMentioned && (
                                    <span className="pill" style={{background:'rgba(34, 197, 94, 0.2)', color:'#22c55e', fontSize:'0.7rem', border:'1px solid #22c55e'}}>
                                      🏷️ Brand Mentioned
                                    </span>
                                  )}
                                  {found && (
                                    <span className="pill" style={{background:'rgba(59, 130, 246, 0.2)', color:'#3b82f6', fontSize:'0.7rem', border:'1px solid #3b82f6'}}>
                                      🎯 Ranked #{result.rank}
                                    </span>
                                  )}
                                  {!found && (
                                    <span className="pill" style={{background:'rgba(239, 68, 68, 0.2)', color:'#ef4444', fontSize:'0.7rem', border:'1px solid #ef4444'}}>
                                      ❌ Not Found
                                    </span>
                                  )}
                                  {result.engine && (
                                    <span className="pill" style={{
                                      background: result.engine === 'perplexity' ? 'rgba(6, 182, 212, 0.2)' : 
                                                 result.engine === 'gemini' ? 'rgba(139, 92, 246, 0.2)' : 
                                                 result.engine === 'openai' ? 'rgba(34, 197, 94, 0.2)' : 
                                                 result.engine === 'claude' ? 'rgba(245, 158, 11, 0.2)' : 
                                                 'rgba(107, 114, 128, 0.2)',
                                      color: result.engine === 'perplexity' ? '#06b6d4' : 
                                            result.engine === 'gemini' ? '#8b5cf6' : 
                                            result.engine === 'openai' ? '#22c55e' : 
                                            result.engine === 'claude' ? '#f59e0b' : 
                                            '#6b7280',
                                      fontSize:'0.7rem', 
                                      border: result.engine === 'perplexity' ? '1px solid #06b6d4' : 
                                             result.engine === 'gemini' ? '1px solid #8b5cf6' : 
                                             result.engine === 'openai' ? '1px solid #22c55e' : 
                                             result.engine === 'claude' ? '1px solid #f59e0b' : 
                                             '1px solid #6b7280'
                                    }}>
                                      {result.engine}
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              <div className="subtle" style={{fontSize:'0.8rem', marginBottom:'0.5rem'}}>
                                Engine: {result.engine?.charAt(0).toUpperCase() + result.engine?.slice(1)}
                              </div>
                              
                              <div className="subtle" style={{fontSize:'0.8rem'}}>
                                Click to view full AI response →
                              </div>
                            </div>
                          );
                        }) || []}
                    </div>
                  </div>
                )}
              </div>
          </Section>
          );
        }

        return (
          <Section title="Run History">
            <div className="subtle" style={{marginBottom:'0.5rem'}}>All past runs (latest first). Click to view details.</div>
            {(analysis || (history && history.length > 0)) ? (
              <div>
                {/* Current analysis */}
                {analysis && (
                  <div className="dashboard-section clickable-run" style={{padding:'1rem', border:'2px solid rgba(59, 130, 246, 0.3)'}}
                       onClick={() => {
                         const currentRunTime = history.length > 0 ? history[history.length - 1].timestamp : new Date().toISOString();
                         setSelectedRun({
                           timestamp: currentRunTime,
                           overall: analysis.metrics?.overall_visibility || 0,
                           perplexity: analysis.metrics?.engine_performance?.perplexity?.visibility_percentage || 0,
                           gemini: analysis.metrics?.engine_performance?.gemini?.visibility_percentage || 0,
                           queries: analysis.generated_queries || [],
                           search_results: analysis.search_results || [],
                           url: analysis.metadata?.url,
                           isCurrent: true
                         });
                       }}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                      <div style={{fontWeight:700, color:'#3b82f6'}}>Current Analysis</div>
                      <div className="badge" style={{fontSize:'0.9rem', padding:'0.4rem 0.8rem'}}>
                        Overall {Math.round(analysis.metrics?.overall_visibility || 0)}%
                      </div>
                    </div>
                    <div className="subtle">
                      {new Date(history.length > 0 ? history[history.length - 1].timestamp : new Date()).toLocaleDateString('en-US', { 
                        month: 'short', day: 'numeric', year: 'numeric' 
                      })} @ {new Date(history.length > 0 ? history[history.length - 1].timestamp : new Date()).toLocaleTimeString('en-US', { 
                        hour12: false, hour: '2-digit', minute: '2-digit' 
                      })}
                    </div>
                    <div className="subtle" style={{marginTop:'0.25rem'}}>
                      Perplexity {Math.round(analysis.metrics?.engine_performance?.perplexity?.visibility_percentage || 0)}% • 
                      Gemini {Math.round(analysis.metrics?.engine_performance?.gemini?.visibility_percentage || 0)}%
                    </div>
                    <div className="subtle" style={{marginTop:'0.5rem', fontSize:'0.8rem'}}>
                      Click to view full query breakdown →
                    </div>
                  </div>
                )}
                
                {/* Historical analyses - most recent first */}
                {history && history.length > 0 && history.slice().reverse().map((p, reversedIdx) => {
                  // Calculate original index for finding previous run
                  const originalIdx = history.length - 1 - reversedIdx;
                  const prevRun = originalIdx > 0 ? history[originalIdx - 1] : null;
                  const getPercentageChange = (current, previous) => {
                    if (!previous) return null;
                    const change = current - previous;
                    return {
                      value: Math.abs(change).toFixed(1),
                      isPositive: change > 0,
                      symbol: change > 0 ? '▲' : '▼'
                    };
                  };
                  
                  const overallChange = getPercentageChange(p.overall, prevRun?.overall);
                  const perplexityChange = getPercentageChange(p.perplexity, prevRun?.perplexity);
                  const geminiChange = getPercentageChange(p.gemini, prevRun?.gemini);
                  
                  return (
                    <div key={p.id || reversedIdx} className="dashboard-section clickable-run" style={{padding:'1rem'}}
                         onClick={() => setSelectedRun(p)}>
                    <div style={{display:'flex', justifyContent:'space-between'}}>
                        <div style={{fontWeight:700}}>
                          {new Date(p.timestamp).toLocaleDateString()} @ {new Date(p.timestamp).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })}
                    </div>
                        <div className="badge">
                          Overall {Math.round(p.overall)}%
                          {overallChange && prevRun && (
                            <span className={overallChange.isPositive ? 'text-green' : 'text-red'} style={{marginLeft:'0.5rem'}}>
                              {overallChange.symbol} {overallChange.value}%
                            </span>
                          )}
                  </div>
                      </div>
                      <div className="subtle">
                        Perplexity {Math.round(p.perplexity||0)}%
                        {perplexityChange && prevRun && (
                          <span className={perplexityChange.isPositive ? 'text-green' : 'text-red'} style={{marginLeft:'0.5rem'}}>
                            {perplexityChange.symbol} {perplexityChange.value}%
                          </span>
                        )}
                        {' • '}
                        Gemini {Math.round(p.gemini||0)}%
                        {geminiChange && prevRun && (
                          <span className={geminiChange.isPositive ? 'text-green' : 'text-red'} style={{marginLeft:'0.5rem'}}>
                            {geminiChange.symbol} {geminiChange.value}%
                          </span>
                        )}
                      </div>
                      <div className="subtle" style={{marginTop:'0.5rem', fontSize:'0.8rem'}}>
                        {p.queries?.length || 0} queries • Click to view details →
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <div className="subtle">No history yet.</div>}
          </Section>
        );
      case 'niches':
        return (
          <Section title="Niches" right={
            <div style={{display:'flex', gap:'0.5rem', alignItems:'center'}}>
              <span className="badge">{niches.length}</span>
              {analysis && (
                <button 
                  className="btn secondary" 
                  style={{fontSize:'0.8rem', padding:'0.5rem 0.75rem'}}
                  onClick={async () => {
                    try {
                      setLoading(true);
                      const resp = await axios.post(`${BACKEND_URL}/cluster-niches`, { analysis_data: analysis });
                      setNiches(resp.data || []);
                      if (resp.data && resp.data.length) setSelectedNiche(resp.data[0].name);
                    } catch (e) {
                      console.error('Failed to regenerate niches', e);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? 'Regenerating...' : 'Regenerate'}
                </button>
              )}
            </div>
          }>
            <div className="actions-row" style={{marginBottom:'0.5rem'}}>
              <select className="auth-input compact-select" value={selectedNiche} onChange={(e)=>setSelectedNiche(e.target.value)}>
                {niches.map(n => (<option key={n.name} value={n.name}>{n.name}</option>))}
              </select>
            </div>
            {currentNiche ? (
              <>
                <div className="subtle" style={{marginBottom:'0.5rem'}}>{currentNiche.description}</div>
                <div style={{marginBottom:'0.5rem'}}>
                  {(currentNiche.keywords||[]).slice(0,10).map(k => (<span className="pill" key={k}>{k}</span>))}
                </div>
                <div className="subtle">Example queries:</div>
                <ul className="list-unstyled">
                  {(currentNiche.queries||[]).slice(0,5).map(q => (<li key={q}>• {q}</li>))}
                </ul>
              </>
            ) : (
              <div className="subtle">No niches yet.</div>
            )}
          </Section>
        );
      case 'drafts':
        return (
          <div>
          <Section title="Generate Drafts">
            <div className="actions-row" style={{marginBottom:'0.75rem'}}>
              <select 
                className="auth-input" 
                value={platform} 
                onChange={(e)=>setPlatform(e.target.value)}
                style={{
                  backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3e%3cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3e%3c/svg%3e")',
                  backgroundPosition: 'right 1rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '3rem',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none'
                }}
              >
                <option value="reddit">Reddit</option>
                <option value="hackernews">Hacker News</option>
                <option value="quora">Quora</option>
                <option value="stackexchange">Stack Exchange</option>
                <option value="blog_comment">Blog Comment</option>
                <option value="email">Email Outreach</option>
              </select>
              <input className="auth-input" placeholder="Tone (optional)" value={tone} onChange={(e)=>setTone(e.target.value)} />
              <button className="btn primary" onClick={()=>{
                setDraft(null); // Clear current draft
                generateDraft(platform);
              }} disabled={genLoading}>{genLoading ? 'Generating…' : 'Generate'}</button>
            </div>
            {draft && (
              <div>
                {/* Title Block */}
                {(() => {
                  // Extract title from the normalized draft structure
                  const title = draft?.title || '';
                  
                  return title ? (
                    <div className="dashboard-section" style={{padding:'1rem', marginBottom:'1rem'}}>
                      <div style={{fontWeight:700, fontSize:'0.9rem', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'0.5rem'}}>
                        Title
                      </div>
                      <div style={{fontWeight:800, fontSize:'1.2rem', color:'#3b82f6', lineHeight:1.3}}>
                        {title}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Body Block */}
                <div className="dashboard-section" style={{padding:'1rem', marginBottom:'1rem'}}>
                  <div style={{fontWeight:700, fontSize:'0.9rem', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'0.5rem'}}>
                    Content
                  </div>
                  <div style={{
                    whiteSpace:'pre-wrap', 
                    lineHeight:1.6, 
                    padding:'1rem',
                    background:'rgba(13, 26, 56, 0.6)',
                    borderRadius:'8px',
                    border:'1px solid rgba(59, 130, 246, 0.2)'
                  }}>
                    {/* Extract content from the normalized draft structure */}
                    {draft?.body || draft?.content || draft?.text || 'No content available'}
                  </div>
                </div>
                
                {/* Suggestions Block */}
                {(() => {
                  // Extract suggestions from the normalized draft structure
                  const suggestions = Array.isArray(draft?.suggestions) ? draft.suggestions : [];
                  
                  return suggestions.length > 0 ? (
                    <div className="dashboard-section" style={{padding:'1rem', marginBottom:'1rem'}}>
                      <div style={{fontWeight:700, fontSize:'0.9rem', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'0.75rem'}}>
                        💡 Suggestions for Improvement
                      </div>
                      <div style={{
                        background:'rgba(59, 130, 246, 0.1)',
                        borderRadius:'8px',
                        padding:'1rem',
                        border:'1px solid rgba(59, 130, 246, 0.2)'
                      }}>
                        {suggestions.map((s,i) => (
                          <div key={i} style={{
                            display:'flex',
                            alignItems:'flex-start',
                            gap:'0.5rem',
                            marginBottom: i < suggestions.length - 1 ? '0.75rem' : '0'
                          }}>
                            <span style={{color:'#3b82f6', fontWeight:600}}>•</span>
                            <span style={{lineHeight:1.5}}>{s}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}
                
                <div style={{display:'flex', gap:'1rem', justifyContent:'flex-end'}}>
                  <button 
                    className="btn secondary"
                    onClick={() => {
                      const textToCopy = draft?.body || draft?.content || draft?.text || 'No content to copy';
                      navigator.clipboard.writeText(textToCopy);
                      // Could add a toast notification here
                    }}
                  >
                    📋 Copy Text
                  </button>
                  <button 
                    className="btn primary"
                    onClick={() => {
                      const platformUrls = {
                        'reddit': 'https://reddit.com/submit',
                        'linkedin': 'https://linkedin.com/sharing/share-offsite',
                        'twitter': 'https://twitter.com/compose/tweet',
                        'facebook': 'https://facebook.com/share',
                        'medium': 'https://medium.com/new-story',
                        'hackernews': 'https://news.ycombinator.com/submit',
                        'stackexchange': 'https://stackoverflow.com/questions/ask',
                        'blog_comment': '#',
                        'email': 'mailto:'
                      };
                      const url = platformUrls[platform] || '#';
                      if (url !== '#') {
                        window.open(url, '_blank');
                      }
                    }}
                    style={{display:'flex', alignItems:'center', gap:'0.5rem'}}
                  >
                    🚀 Post to {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </button>
                </div>
              </div>
            )}
          </Section>

            {draftHistory && draftHistory.length > 0 && (
              <Section title="Draft History" right={<span className="badge">{draftHistory.length}</span>}>
                <div style={{marginBottom:'0.5rem', fontSize:'0.9rem', color:'#94a3b8'}}>
                  Previous drafts (latest first)
                </div>
                {draftHistory.slice(0, 10).map((historyDraft, idx) => (
                  <div key={historyDraft.id} className="dashboard-section" style={{padding:'1rem', marginBottom:'0.5rem'}}>
                    <div style={{display:'flex', justifyContent:'space-between', marginBottom:'0.5rem'}}>
                      <div style={{display:'flex', gap:'0.5rem', alignItems:'center'}}>
                        <span className="badge">{historyDraft.platform}</span>
                        {historyDraft.niche && <span className="pill">{historyDraft.niche}</span>}
                      </div>
                      <div className="subtle" style={{fontSize:'0.8rem'}}>
                        {new Date(historyDraft.timestamp).toLocaleDateString()}
                      </div>
                    </div>
                    {historyDraft.content?.title && (
                      <div style={{fontWeight:600, marginBottom:'0.25rem'}}>{historyDraft.content.title}</div>
                    )}
                    <div style={{
                      fontSize:'0.9rem', 
                      lineHeight:1.5, 
                      overflow:'hidden', 
                      textOverflow:'ellipsis',
                      display:'-webkit-box',
                      WebkitLineClamp:3,
                      WebkitBoxOrient:'vertical'
                    }}>
                      {historyDraft.content?.body || historyDraft.content?.content || historyDraft.content?.text || 'No content preview available'}
                    </div>
                                        <button 
                      className="btn secondary"
                      style={{marginTop:'0.5rem', fontSize:'0.8rem', padding:'0.25rem 0.5rem'}}
                      onClick={() => {
                        setDraft(historyDraft.content);
                        setPlatform(historyDraft.platform);
                        setTone(historyDraft.tone || '');
                        if (historyDraft.niche) setSelectedNiche(historyDraft.niche);
                      }}
                    >
                      Load Draft
                    </button>
                  </div>
                ))}
              </Section>
            )}
            </div>
        );
      case 'analytics':
              return (
          <div>
            <Section title="GA4 Analytics Configuration">
              <div style={{marginBottom:'1rem', fontSize:'0.9rem', color:'#94a3b8'}}>
                Connect your Google Analytics 4 to analyze AI search traffic patterns
                          </div>
              
              <div className="two-col" style={{marginBottom:'1.5rem'}}>
                <div className="form-group">
                  <label style={{display:'block', marginBottom:'0.5rem', fontWeight:600}}>
                    Measurement ID (e.g., G-XXXXXXXXXX)
                  </label>
                  <input
                    type="text"
                    className="auth-input"
                    placeholder="G-XXXXXXXXXX"
                    value={ga4Config.measurementId}
                    onChange={(e) => saveGA4Config({...ga4Config, measurementId: e.target.value})}
                  />
                        </div>
                
                <div className="form-group">
                  <label style={{display:'block', marginBottom:'0.5rem', fontWeight:600}}>
                    Property ID (numeric)
                  </label>
                  <input
                    type="text"
                    className="auth-input"
                    placeholder="123456789"
                    value={ga4Config.propertyId}
                    onChange={(e) => saveGA4Config({...ga4Config, propertyId: e.target.value})}
                  />
                  </div>
              </div>
              
              <div className="form-group" style={{marginBottom:'1.5rem'}}>
                <label style={{display:'block', marginBottom:'0.5rem', fontWeight:600}}>
                  Google Analytics API Key
                </label>
                <input
                  type="password"
                  className="auth-input"
                  placeholder="Your GA4 API key"
                  value={ga4Config.apiKey}
                  onChange={(e) => saveGA4Config({...ga4Config, apiKey: e.target.value})}
                />
                <div className="subtle" style={{marginTop:'0.25rem', fontSize:'0.8rem'}}>
                  Get your API key from Google Cloud Console → APIs & Services → Credentials
                </div>
              </div>
              
              <button 
                className="btn primary" 
                onClick={fetchGA4Data}
                disabled={ga4Loading || !ga4Config.measurementId || !ga4Config.propertyId || !ga4Config.apiKey}
                style={{display:'flex', alignItems:'center', gap:'0.5rem'}}
              >
                {ga4Loading && <div className="loading-spinner-circle"></div>}
                {ga4Loading ? 'Loading Analytics...' : 'Fetch AI Search Traffic'}
              </button>
            </Section>
            
            {ga4Data && (
              <Section title="AI Search Traffic Analysis">
                {ga4Data.error ? (
                  <div style={{color:'#ef4444', padding:'1rem', background:'rgba(239, 68, 68, 0.1)', borderRadius:'8px'}}>
                    {ga4Data.error}
                  </div>
                ) : (
                  <div>
                    <div className="two-col" style={{marginBottom:'1.5rem'}}>
                      <div className="metric-card">
                        <div className="metric-value">{ga4Data.aiSearchSessions || 0}</div>
                        <div className="metric-label">AI Search Sessions</div>
                        <div className="subtle">Sessions from AI search engines</div>
                      </div>
                      
                      <div className="metric-card">
                        <div className="metric-value">{ga4Data.aiSearchUsers || 0}</div>
                        <div className="metric-label">Unique AI Users</div>
                        <div className="subtle">Users coming from AI platforms</div>
                      </div>
                      
                      <div className="metric-card">
                        <div className="metric-value">{ga4Data.conversionRate || '0'}%</div>
                        <div className="metric-label">AI Traffic Conversion</div>
                        <div className="subtle">Conversion rate from AI traffic</div>
                      </div>
                      
                      <div className="metric-card">
                        <div className="metric-value">{ga4Data.avgSessionDuration || '0s'}</div>
                        <div className="metric-label">Avg Session Duration</div>
                        <div className="subtle">Time spent by AI users</div>
                      </div>
                    </div>
                    
                    {ga4Data.topAIQueries && ga4Data.topAIQueries.length > 0 && (
                      <div className="dashboard-section" style={{padding:'1rem'}}>
                        <h4 style={{marginBottom:'1rem'}}>Top AI Search Queries Driving Traffic</h4>
                        {ga4Data.topAIQueries.map((query, idx) => (
                          <div key={idx} style={{
                            display:'flex', 
                            justifyContent:'space-between', 
                            padding:'0.75rem 0',
                            borderBottom: idx < ga4Data.topAIQueries.length - 1 ? '1px solid rgba(51,65,85,0.3)' : 'none'
                          }}>
                            <div>
                              <div style={{fontWeight:600}}>"{query.query}"</div>
                              <div className="subtle" style={{fontSize:'0.8rem'}}>
                                {query.source} • {query.sessions} sessions
                              </div>
                            </div>
                            <div style={{textAlign:'right'}}>
                              <div className="badge">{query.users} users</div>
                              <div className="subtle" style={{fontSize:'0.8rem'}}>
                                {query.bounceRate}% bounce rate
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {ga4Data.aiTrafficTrend && (
                      <div className="dashboard-section" style={{padding:'1rem'}}>
                        <h4 style={{marginBottom:'1rem'}}>AI Search Traffic Trend (30 days)</h4>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={ga4Data.aiTrafficTrend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                            <XAxis dataKey="date" stroke="#94a3b8" />
                            <YAxis stroke="#94a3b8" />
                            <Tooltip 
                              contentStyle={{
                                backgroundColor: 'rgba(30, 41, 59, 0.95)',
                                border: '1px solid #3b82f6',
                                borderRadius: '8px',
                                color: '#f8fafc'
                              }}
                            />
                            <Legend />
                            <Line type="monotone" dataKey="sessions" stroke="#3b82f6" strokeWidth={2} name="AI Sessions" />
                            <Line type="monotone" dataKey="users" stroke="#06b6d4" strokeWidth={2} name="AI Users" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                )}
              </Section>
            )}
                </div>
              );
      default:
        return <div>Tab not found</div>;
    }
  };

  return (
    <div className="container dashboard-page">
      <div style={{marginBottom:'1rem'}}>
        <h2 className="section-title" style={{textAlign:'left', margin:0}}>Dashboard</h2>
        <a className="glow-link" href={analysis?.metadata?.url} target="_blank" rel="noreferrer" style={{fontSize:'1.1rem', fontWeight:700}}>
          {analysis?.metadata?.url}
        </a>
      </div>
      
      <div style={{marginBottom:'1rem', display:'flex', justifyContent:'flex-end'}}>
        <button 
          onClick={runAnalysis} 
          disabled={rerunning}
          className="btn primary rerun-btn-main"
          style={{display:'flex', alignItems:'center', gap:'0.5rem'}}
        >
          {rerunning && <div className="loading-spinner-circle"></div>}
          {rerunning ? 'Running Analysis...' : 'Rerun Analysis'}
        </button>
      </div>
      <div className="dashboard-grid">
        <aside className="dashboard-sidebar">
          <Section title="Key Metrics">
            <div className="two-col">
              {kpis.map((k) => (
                <div key={k.label} className="metric-card" style={{padding:'1rem', minWidth:'unset'}}>
                  <div className="metric-value" style={{fontSize:'1.4rem'}}>{k.value}</div>
                  <div className="metric-label" style={{
                    textTransform:'none', 
                    whiteSpace: (k.label === 'Overall Visibility' || k.label === 'Weighted Score') ? 'normal' : 'nowrap',
                    lineHeight: '1.2',
                    fontSize: (k.label === 'Overall Visibility' || k.label === 'Weighted Score') ? '0.8rem' : '0.9rem'
                  }}>{k.label}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Navigation">
            <div className="dashboard-nav">
              {[
                { id: 'home', label: 'Home', icon: '' },
                { id: 'history', label: 'Run History', icon: '' },
                { id: 'sources', label: 'External References', icon: '' },
                { id: 'niches', label: 'Niches', icon: '' },
                { id: 'analytics', label: 'GA4 Analytics', icon: '' },
                { id: 'drafts', label: 'Drafts', icon: '' }
              ].map(tab => (
                <button 
                  key={tab.id}
                  className={`dashboard-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="nav-icon">{tab.icon}</span>
                  <span className="nav-label">{tab.label}</span>
                </button>
              ))}
            </div>
          </Section>
        </aside>

        <main>
          {renderTabContent()}
        </main>
      </div>

          {showModal && (
            <div className="modal-overlay" onClick={()=>setShowModal(false)}>
              <div className="modal-content" onClick={(e)=>e.stopPropagation()}>
                <button className="modal-close" onClick={()=>setShowModal(false)}>×</button>
                <div className="response-header">{modalHeader}</div>
                <div className="response-text" dangerouslySetInnerHTML={{ __html: modalText }}>
                </div>
              </div>
            </div>
          )}
    </div>
  );
}


